import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserRole, Permission, RolePermissions } from '@/types/global.types';
import { AuthStore, LoginCredentials } from '../types/auth.types';
import { authService } from '../services/authService';
import { fetchMyPermissions } from '@/services/permissionsService';
import { fetchSessionSnapshot } from '@/services/sessionService';
import { queryClient } from '@/lib/queryClient';

// Session re-sync is cheap but runs on every protected route mount: throttle
// it unless a caller forces it (e.g. right after a token refresh).
const SESSION_SYNC_MIN_INTERVAL_MS = 60_000;
let lastSessionSyncAt = 0;
let permissionsInFlight: Promise<void> | null = null;

// Roles that DG can "View As"
const VIEW_AS_ALLOWED_ROLES: UserRole[] = [
  UserRole.PEDAGOGICAL_DIRECTOR,
  UserRole.FINANCIAL_MANAGER,
  UserRole.MARKETING_MANAGER,
  UserRole.COMMERCIAL_MANAGER,
  UserRole.BRANCH_MANAGER,
];

// Extended store type with validation modal states
interface ValidationModalState {
  welcomeModalDismissed: boolean;
  rejectedModalDismissed: boolean;
  approvedBannerDismissed: boolean;
  lastKnownStatus: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED' | null;
}

interface ValidationModalActions {
  dismissWelcomeModal: () => void;
  dismissRejectedModal: () => void;
  dismissApprovedBanner: () => void;
  resetModalStates: () => void;
  checkStatusChange: (newStatus: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED') => boolean;
}

// Permissions state
interface PermissionsState {
  permissions: Permission[];
  permissionsLoaded: boolean;
}

interface PermissionsActions {
  fetchPermissions: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  setPermissions: (permissions: Permission[]) => void;
}

type ExtendedAuthStore = AuthStore & ValidationModalState & ValidationModalActions & PermissionsState & PermissionsActions;

export const useAuthStore = create<ExtendedAuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      viewAsRole: null,

      // Validation modal states
      welcomeModalDismissed: false,
      rejectedModalDismissed: false,
      approvedBannerDismissed: false,
      lastKnownStatus: null,

      // Permissions state
      permissions: [],
      permissionsLoaded: false,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);

          // Backend now returns correct firstName/lastName from User table
          // (synced when user saves their profile with nom/prenom)
          authService.saveTokens(response.tokens);
          authService.saveUser(response.user);

          // Clear financial suspension flag on successful login
          // (if account was unsuspended, allow access)
          localStorage.removeItem('nexedu_financial_suspended');
          localStorage.removeItem('nexedu_financial_suspension_message');

          // Reset modal states on login - each user should see modals fresh
          // based on their own profile status
          const profileStatus = response.user.profileSubmissionStatus || 'DRAFT';

          // Use permissions from login response if available, otherwise fall back to role defaults
          const userPermissions = (response.user as { permissions?: string[] }).permissions;
          const hasLoginPermissions = userPermissions && userPermissions.length > 0;

          set({
            user: response.user,
            tokens: response.tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            viewAsRole: null,
            // Reset modal states for new login session
            welcomeModalDismissed: false,
            rejectedModalDismissed: false,
            approvedBannerDismissed: false,
            lastKnownStatus: profileStatus,
            // Use permissions from login response if available
            permissions: hasLoginPermissions
              ? (userPermissions as Permission[])
              : (RolePermissions[response.user.role] || []),
            permissionsLoaded: hasLoginPermissions,
          });

          // Only fetch permissions from API if not already provided in login response
          if (!hasLoginPermissions) {
            get().fetchPermissions().catch(() => {
              // If fetch fails, keep using role-based defaults
              console.warn('Failed to fetch permissions from API, using role defaults');
            });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Erreur de connexion';
          set({
            isLoading: false,
            error: message,
            isAuthenticated: false,
            user: null,
            tokens: null,
          });
          throw error;
        }
      },

      logout: () => {
        authService.clearTokens();
        // Clear ALL cached server state so the next user never sees the
        // previous user's data (dashboards, lists, notifications, ...)
        queryClient.clear();
        // Also clear the zustand persist storage to prevent stale data
        localStorage.removeItem('nexedu-auth-storage');
        // Clear financial suspension flags
        localStorage.removeItem('nexedu_financial_suspended');
        localStorage.removeItem('nexedu_financial_suspension_message');
        // Clear any profile-related localStorage items to prevent stale data on next login
        // This ensures a new user doesn't see the previous user's profile data
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('profile_') || key.startsWith('userProfile_'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          viewAsRole: null,
          // Reset modal states on logout
          welcomeModalDismissed: false,
          rejectedModalDismissed: false,
          approvedBannerDismissed: false,
          lastKnownStatus: null,
          // Reset permissions on logout
          permissions: [],
          permissionsLoaded: false,
        });
      },

      refreshToken: async () => {
        const refreshToken = authService.getRefreshToken();
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await authService.refreshToken(refreshToken);
          set((state) => ({
            tokens: state.tokens
              ? { ...state.tokens, accessToken: response.accessToken }
              : null,
          }));
          authService.saveTokens({ accessToken: response.accessToken });
        } catch {
          get().logout();
        }
      },

      clearError: () => {
        set({ error: null });
      },

      // The user record (and its role) is saved at login and would otherwise
      // never be re-read. Called on app load and after every token refresh so
      // that an admin's role change reaches an open session.
      syncSessionFromServer: async (options?: { force?: boolean }) => {
        const { user } = get();
        if (!user) return { roleChanged: false, role: null };
        if (!options?.force && Date.now() - lastSessionSyncAt < SESSION_SYNC_MIN_INTERVAL_MS) {
          return { roleChanged: false, role: user.role };
        }
        lastSessionSyncAt = Date.now();

        let snapshot;
        try {
          snapshot = await fetchSessionSnapshot();
        } catch {
          // Network or auth failure: keep the cached session, nothing to sync
          return { roleChanged: false, role: user.role };
        }

        const roleChanged = snapshot.role !== user.role;
        const changed =
          roleChanged ||
          snapshot.status !== user.status ||
          snapshot.isAlsoProfessor !== (user.isAlsoProfessor ?? false) ||
          (snapshot.enabledSolutions ?? []).join(',') !== (user.enabledSolutions ?? []).join(',');

        if (!changed) return { roleChanged: false, role: user.role };

        const updatedUser = {
          ...user,
          role: snapshot.role,
          status: snapshot.status,
          isAlsoProfessor: snapshot.isAlsoProfessor,
          enabledSolutions: snapshot.enabledSolutions,
          firstName: snapshot.firstName,
          lastName: snapshot.lastName,
        };
        set({
          user: updatedUser,
          // A DG "view as" selection is meaningless once the role changed
          ...(roleChanged && { viewAsRole: null }),
          permissions: RolePermissions[updatedUser.role] || [],
          permissionsLoaded: false,
        });
        authService.saveUser(updatedUser);

        if (roleChanged) {
          // The access token still carries the OLD role (backend guards read
          // it), so get a fresh pair before any role-gated call is made.
          const refreshToken = authService.getRefreshToken();
          if (refreshToken) {
            try {
              const tokens = await authService.refreshToken(refreshToken);
              authService.saveTokens(tokens);
              set((state) => ({
                tokens: state.tokens ? { ...state.tokens, ...tokens } : null,
              }));
            } catch {
              // Keep the session: the next 401 triggers the interceptor's refresh
            }
          }
          queryClient.clear();
        }

        await get().fetchPermissions().catch(() => {
          console.warn('Failed to fetch permissions after session sync, using role defaults');
        });

        return { roleChanged, role: updatedUser.role };
      },

      setViewAsRole: (role: UserRole | null) => {
        const { user } = get();

        // Only DG can use View As
        if (user?.role !== UserRole.DIRECTOR_GENERAL) {
          return;
        }

        // Validate allowed roles
        if (role !== null && !VIEW_AS_ALLOWED_ROLES.includes(role)) {
          return;
        }

        set({ viewAsRole: role });

        // Save to localStorage for persistence
        if (role) {
          localStorage.setItem('nexedu_view_as_role', role);
        } else {
          localStorage.removeItem('nexedu_view_as_role');
        }
      },

      getEffectiveRole: (): UserRole | null => {
        const { user, viewAsRole } = get();
        if (!user) return null;

        // If DG is viewing as another role, return that role
        if (user.role === UserRole.DIRECTOR_GENERAL && viewAsRole) {
          return viewAsRole;
        }

        return user.role;
      },

      // Update user profile data (name, email, etc.)
      updateUserProfile: (updates: { firstName?: string; lastName?: string; email?: string; mustChangePassword?: boolean }) => {
        const { user } = get();
        if (!user) return;

        const updatedUser = {
          ...user,
          ...(updates.firstName && { firstName: updates.firstName }),
          ...(updates.lastName && { lastName: updates.lastName }),
          ...(updates.email && { email: updates.email }),
          // Booleans must be merged explicitly — this flag being dropped kept
          // the forced password-change modal reappearing on every refresh
          ...(updates.mustChangePassword !== undefined && { mustChangePassword: updates.mustChangePassword }),
        };

        // Update state
        set({ user: updatedUser });

        // Save to localStorage
        authService.saveUser(updatedUser);
      },

      // Update tenant logo URL (called after logo upload/delete)
      updateTenantLogo: (logoUrl: string | undefined) => {
        const { user } = get();
        if (!user) return;

        const updatedUser = {
          ...user,
          tenantLogoUrl: logoUrl,
        };

        // Update state
        set({ user: updatedUser });

        // Save to localStorage
        authService.saveUser(updatedUser);
      },

      // Update profile submission status (called after profile submission or validation)
      updateProfileSubmissionStatus: (status: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED') => {
        const { user } = get();
        if (!user) return;

        const updatedUser = {
          ...user,
          profileSubmissionStatus: status,
        };

        // Update state
        set({ user: updatedUser });

        // Save to localStorage
        authService.saveUser(updatedUser);
      },

      // Update profile completed flag (called when user completes their profile form)
      updateProfileCompleted: (completed: boolean) => {
        const { user } = get();
        if (!user) return;

        const updatedUser = {
          ...user,
          profileCompleted: completed,
        };

        // Update state
        set({ user: updatedUser });

        // Save to localStorage
        authService.saveUser(updatedUser);
      },

      // Set auth state directly (used by VerifyEmailPage after password setup)
      setAuth: (user, tokens) => {
        authService.saveTokens(tokens);
        authService.saveUser(user);

        const profileStatus = user.profileSubmissionStatus || 'DRAFT';

        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          viewAsRole: null,
          welcomeModalDismissed: false,
          rejectedModalDismissed: false,
          approvedBannerDismissed: false,
          lastKnownStatus: profileStatus,
          permissions: RolePermissions[user.role] || [],
          permissionsLoaded: false,
        });

        // Fetch permissions from API in background
        get().fetchPermissions().catch(() => {
          console.warn('Failed to fetch permissions from API, using role defaults');
        });
      },

      // Validation modal actions
      dismissWelcomeModal: () => {
        set({ welcomeModalDismissed: true });
      },

      dismissRejectedModal: () => {
        set({ rejectedModalDismissed: true });
      },

      dismissApprovedBanner: () => {
        set({ approvedBannerDismissed: true });
      },

      resetModalStates: () => {
        set({
          welcomeModalDismissed: false,
          rejectedModalDismissed: false,
          approvedBannerDismissed: false,
        });
      },

      // Check if status changed (for showing modals on status transitions)
      checkStatusChange: (newStatus: 'DRAFT' | 'PENDING_VALIDATION' | 'APPROVED' | 'REJECTED') => {
        const { lastKnownStatus } = get();

        if (lastKnownStatus !== newStatus) {
          // Status changed - reset modal dismissed states for relevant modals
          if (newStatus === 'REJECTED') {
            set({ rejectedModalDismissed: false, lastKnownStatus: newStatus });
          } else if (newStatus === 'APPROVED' && lastKnownStatus === 'PENDING_VALIDATION') {
            set({ approvedBannerDismissed: false, lastKnownStatus: newStatus });
          } else {
            set({ lastKnownStatus: newStatus });
          }
          return true; // Status changed
        }
        return false; // No change
      },

      // Permission actions
      fetchPermissions: async () => {
        // One request at a time: concurrent callers (init, tab sync, pages) share it.
        if (permissionsInFlight) return permissionsInFlight;
        permissionsInFlight = (async () => {
        try {
          const response = await fetchMyPermissions();
          set({
            permissions: response.permissions,
            permissionsLoaded: true,
          });
        } catch (error) {
          const { user, permissions, permissionsLoaded } = get();
          // A transient failure (rate limit, restart) must not downgrade the UI:
          // keep what the server last granted. Fall back to role defaults only
          // when nothing was ever loaded in this session.
          if (permissionsLoaded && permissions.length > 0) throw error;
          if (user) {
            set({
              permissions: RolePermissions[user.role] || [],
              permissionsLoaded: true,
            });
          }
          throw error;
        } finally {
          permissionsInFlight = null;
        }
        })();
        return permissionsInFlight;
      },

      hasPermission: (permission: Permission) => {
        const { permissions, user, permissionsLoaded } = get();

        // If permissions not loaded yet OR permissions array is empty, use role defaults
        if ((!permissionsLoaded || permissions.length === 0) && user) {
          return (RolePermissions[user.role] || []).includes(permission);
        }

        return permissions.includes(permission);
      },

      hasAnyPermission: (permissionList: Permission[]) => {
        const { permissions, user, permissionsLoaded } = get();

        // If permissions not loaded yet OR permissions array is empty, use role defaults
        const effectivePermissions = (!permissionsLoaded || permissions.length === 0) && user
          ? (RolePermissions[user.role] || [])
          : permissions;

        return permissionList.some(p => effectivePermissions.includes(p));
      },

      hasAllPermissions: (permissionList: Permission[]) => {
        const { permissions, user, permissionsLoaded } = get();

        // If permissions not loaded yet OR permissions array is empty, use role defaults
        const effectivePermissions = (!permissionsLoaded || permissions.length === 0) && user
          ? (RolePermissions[user.role] || [])
          : permissions;

        return permissionList.every(p => effectivePermissions.includes(p));
      },

      setPermissions: (newPermissions: Permission[]) => {
        set({
          permissions: newPermissions,
          permissionsLoaded: true,
        });
      },
    }),
    {
      name: 'nexedu-auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        viewAsRole: state.viewAsRole,
        // Persist validation modal states
        welcomeModalDismissed: state.welcomeModalDismissed,
        rejectedModalDismissed: state.rejectedModalDismissed,
        approvedBannerDismissed: state.approvedBannerDismissed,
        lastKnownStatus: state.lastKnownStatus,
        // NOTE: Do NOT persist permissions - they should always be fetched fresh from API
        // This ensures users always get their latest permissions after admin changes
      }),
      // Use custom merge to ensure fresh login data always wins
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ExtendedAuthStore>;
        return {
          ...currentState,
          ...persisted,
          // Don't restore loading/error states from cache
          isLoading: false,
          error: null,
          // IMPORTANT: Force permissions to be re-fetched from API on page reload
          // This ensures users always get their latest permissions after admin changes
          permissionsLoaded: false,
        };
      },
    }
  )
);

// Initialize from localStorage on app start
export const initializeAuth = (): void => {
  const savedUser = authService.getSavedUser();
  const accessToken = authService.getAccessToken();
  const refreshToken = authService.getRefreshToken();
  const viewAsRole = localStorage.getItem('nexedu_view_as_role') as UserRole | null;

  if (savedUser && accessToken) {
    // Idempotent: when the store already holds this exact session, do nothing.
    // Every setState here is persisted to localStorage, which fires a 'storage'
    // event in every OTHER tab, whose handler calls initializeAuth again. Two
    // tabs whose in-memory state differed in any persisted flag ping-ponged
    // forever, each bounce fetching permissions, until the API rate limit hit.
    const current = useAuthStore.getState();
    const sameSession =
      current.isAuthenticated &&
      current.user?.id === savedUser.id &&
      current.user?.role === savedUser.role &&
      current.tokens?.accessToken === accessToken &&
      (current.tokens?.refreshToken ?? null) === refreshToken;
    if (sameSession) {
      // Nothing to write, but permissions are never persisted: on a page
      // reload they must still be fetched once or RequirePermission routes
      // (e.g. Emplois du Temps) spin forever waiting for permissionsLoaded.
      if (!current.permissionsLoaded) {
        current.fetchPermissions().catch(() => {
          console.warn('Failed to fetch permissions on init, using role defaults');
        });
      }
      return;
    }

    useAuthStore.setState({
      user: savedUser,
      tokens: { accessToken, refreshToken: refreshToken || undefined },
      isAuthenticated: true,
      viewAsRole:
        savedUser.role === UserRole.DIRECTOR_GENERAL && viewAsRole
          ? viewAsRole
          : null,
    });

    // Fetch fresh permissions from API after restoring session
    // This ensures users always have their latest permissions
    useAuthStore.getState().fetchPermissions().catch(() => {
      console.warn('Failed to fetch permissions on init, using role defaults');
    });
  }
};
